import {
  IntegrationLogResponse,
  IntegrationLogStatusEnum,
} from '@monetize/types/app';
import { createColumnHelper } from '../../../../components/Monetize/DataTableNew/appTable';
import { MText, MTooltip } from '../../../../components/Monetize';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  dateTimeBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  textBodyTemplate,
} from '../../../../components/Monetize/DataTableNew/tableUtilsNew';
import {
  EntityNameEnumDisplay,
  EventTypeEnumDisplay,
  ExternalRecordLinkConfig,
  IntegrationLogStatusTagStyle,
  IntegrationStatusEnumDisplay,
  SourceIdCell,
  TargetIdCell,
  TargetSystemTypeEnumDisplay,
} from '../../../../components/IntegrationLog';
import { objectToObjArray } from '../../../../utils/misc';

export interface IntegrationLogColumnContext extends ColumnContextBase<IntegrationLogResponse> {
  /** Only Salesforce and HubSpot record URLs are constructible today — see getExternalRecordUrl. */
  linkConfig: ExternalRecordLinkConfig;
}

const columnHelper = createColumnHelper<IntegrationLogResponse>();

// Filter values come from the display maps so the popover shows labels while
// filtering on the raw values the API expects.
const entityFilterValues = objectToObjArray(
  EntityNameEnumDisplay,
  'value',
  'label',
);
const eventFilterValues = objectToObjArray(
  EventTypeEnumDisplay,
  'value',
  'label',
);
const statusFilterValues = objectToObjArray(
  IntegrationStatusEnumDisplay,
  'value',
  'label',
);
const targetSystemFilterValues = objectToObjArray(
  TargetSystemTypeEnumDisplay,
  'value',
  'label',
);

/**
 * The list endpoint supports no sorting, so every column opts out rather than
 * offering a header affordance that does nothing.
 */
export const INTEGRATION_LOG_CATALOG: ColumnCatalog<
  IntegrationLogResponse,
  IntegrationLogColumnContext
> = [
  {
    id: 'lastUpdatedAt',
    label: 'Timestamp',
    build: () =>
      columnHelper.accessor('lastUpdatedAt', {
        ...getColHeaderWithSize('Timestamp', 200),
        // The API takes two inclusive bounds (fromLastUpdatedAt /
        // toLastUpdatedAt); useIntegrationLogData expands this filter into them.
        ...getColHeaderFilters('DATE', { dateFilter: { includeTime: true } }),
        cell: dateTimeBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'entityName',
    label: 'Object',
    build: () =>
      columnHelper.accessor('entityName', {
        ...getColHeaderWithSize('Object', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: entityFilterValues,
        }),
        cell: textBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'eventType',
    label: 'Operation',
    build: () =>
      columnHelper.accessor('eventType', {
        ...getColHeaderWithSize('Operation', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: eventFilterValues,
        }),
        cell: textBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'label',
    label: 'Label',
    build: () =>
      columnHelper.accessor('label', {
        ...getColHeaderWithSize('Label', 200),
        cell: ({ row }) => (
          <MTooltip label={row.original.label} placement="bottom-end">
            <MText lineClamp={1}>{row.original.label}</MText>
          </MTooltip>
        ),
        enableSorting: false,
      }),
  },
  {
    id: 'targetSystemType',
    label: 'External System',
    build: () =>
      columnHelper.accessor('targetSystemType', {
        ...getColHeaderWithSize('External System', 220),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: targetSystemFilterValues,
        }),
        cell: ({ row }) => {
          const { targetSystemType } = row.original;
          return (
            <MText>
              {TargetSystemTypeEnumDisplay[targetSystemType] ||
                targetSystemType}
            </MText>
          );
        },
        enableSorting: false,
      }),
  },
  {
    id: 'sourceId',
    label: 'Source Record ID',
    build: () =>
      columnHelper.accessor('sourceId', {
        ...getColHeaderWithSize('Source Record ID', 230),
        // The API only matches source ids exactly — it has no `contains` support
        ...getColHeaderFilters('TEXT', { textFilter: { operator: 'EQUAL' } }),
        cell: ({ row }) => <SourceIdCell sourceId={row.original.sourceId} />,
        enableSorting: false,
      }),
  },
  {
    id: 'targetId',
    label: 'Target Record ID',
    build: ({ linkConfig }) =>
      columnHelper.accessor('targetId', {
        ...getColHeaderWithSize('Target Record ID', 200),
        cell: ({ row }) => (
          <TargetIdCell
            targetId={row.original.targetId}
            targetSystemType={row.original.targetSystemType}
            entityName={row.original.entityName}
            linkConfig={linkConfig}
            textProps={{ lineClamp: 3 }}
          />
        ),
        enableSorting: false,
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: statusFilterValues,
        }),
        cell: ({ row }) => {
          const { status, log } = row.original;
          const statusDisplay = IntegrationStatusEnumDisplay[status];
          const statusStyle = IntegrationLogStatusTagStyle[status] ?? {};
          if (!statusDisplay) {
            return null;
          }
          const content = (
            <MText
              lineClamp={1}
              {...statusStyle}
              maxW="fit-content"
              h="18px"
              lineHeight="18px"
            >
              {statusDisplay}
            </MText>
          );
          if (status === IntegrationLogStatusEnum.FAILED) {
            return <MTooltip label={log}>{content}</MTooltip>;
          }
          return content;
        },
        enableSorting: false,
      }),
  },
];

/** Stub context with no external record link config, for callers that don't need the link. */
export const INTEGRATION_LOG_COLUMN_CONTEXT: IntegrationLogColumnContext = {
  linkConfig: {},
};
